import React from "react";
import { Stack } from "@bookingcom/bui-react";
import useExtraProductsInfoController from "./useExtraProductsInfo.controller";
import { InconsistentCabinBaggageInfo } from "../../../../../../../ancillaries/components/ExtraProducts/components/InconsistentCabinBaggageInfo";
import { BaggageItem } from "../../../../../../../ancillaries/components/ExtraProducts/components/BaggageItem";
import FlexTicketItem from "../../../../../../../ancillaries/components/ExtraProducts/components/FlexTicketItem";
import InsuranceItem from "../../../../../../../ancillaries/components/ExtraProducts/components/InsuranceItem";
import flights_web_condensed_extras_fd_ddot from "../../../../../../../utils/experiments/funnel/flights_web_condensed_extras_fd_ddot";
import useProfiler from "../../../../../../../hooks/useProfiler";
import flights_web_condensed_extras_fd_mdot from "../../../../../../../utils/experiments/funnel/flights_web_condensed_extras_fd_mdot";
import { UIFlightData } from "@flights/types";
import flights_apex_web_remove_price_qualifier_for_solo_traveler_fd from "../../../../../../../utils/experiments/apex/flights_apex_web_remove_price_qualifier_for_solo_traveler_fd";

const useExtraProductsInfo = (flight: UIFlightData) => {
  const {
    header,
    subheader,
    hasData,
    ancillaries,
    showLoadingPrice,
    inconsistentCabinBaggageInfo,
    cabinBaggagePerTraveller,
    checkedInBaggage,
    flexTicket,
    insurance,
    extrasRef
  } = useExtraProductsInfoController(flight);
  const { isMobile, isMeta, isSolo } = useProfiler();

  if (!hasData || !ancillaries) return undefined;

  const condensedFDDesktop = !isMobile() && !!flights_web_condensed_extras_fd_ddot.trackWithDefaultStage();
  const condensedFDMobile = isMobile() && !!flights_web_condensed_extras_fd_mdot.trackWithDefaultStage();

  const canHideQualifier = isSolo() && !!(flexTicket || insurance);
  const hideQualifier =
    canHideQualifier && !!flights_apex_web_remove_price_qualifier_for_solo_traveler_fd.trackWithDefaultStage();

  if (canHideQualifier) {
    flights_apex_web_remove_price_qualifier_for_solo_traveler_fd.trackStages({
      isMobile: isMobile(),
      isMeta: isMeta(),
      hasFlex: !!flexTicket,
      hasInsurance: !!insurance
    });
  }
  /* end - flights_apex_web_remove_price_qualifier_for_solo_traveler_fd */

  return {
    header,
    subheader,
    content: (
      <Stack gap={2} attributes={{ ref: extrasRef }}>
        {inconsistentCabinBaggageInfo && (
          <InconsistentCabinBaggageInfo includedProducts={inconsistentCabinBaggageInfo} ancillaries={ancillaries} />
        )}

        {cabinBaggagePerTraveller && (
          <BaggageItem
            type="cabinBaggagePerTraveller"
            product={cabinBaggagePerTraveller}
            showLoadingPrice={showLoadingPrice}
            dataTestId="extra_baggage_cabin"
            condensedFDDesktop={condensedFDDesktop}
            condensedFDMobile={condensedFDMobile}
          />
        )}

        {checkedInBaggage && (
          /* On flight details we only get one checked baggage option which is the cheapest one */
          <BaggageItem
            type="checkedInBaggage"
            product={checkedInBaggage}
            showLoadingPrice={showLoadingPrice}
            dataTestId="extra_baggage_checkedin"
            condensedFDDesktop={condensedFDDesktop}
            condensedFDMobile={condensedFDMobile}
          />
        )}

        {flexTicket && (
          <span>
            <FlexTicketItem
              flexibleTicket={flexTicket}
              showLoadingPrice={showLoadingPrice}
              condensedFDDesktop={condensedFDDesktop}
              condensedFDMobile={condensedFDMobile}
              hideQualifier={hideQualifier}
            />
          </span>
        )}

        {insurance && (
          <InsuranceItem
            travelInsurance={insurance}
            showLoadingPrice={showLoadingPrice}
            condensedFDDesktop={condensedFDDesktop}
            condensedFDMobile={condensedFDMobile}
            hideQualifier={hideQualifier}
          />
        )}
      </Stack>
    )
  };
};

export default useExtraProductsInfo;
